import React, { useRef, useMemo } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { OrbitControls, Box, Line, Text, Plane, Cylinder } from '@react-three/drei';
import * as THREE from 'three';

const W = 800;
const H = 600;

// Utility to convert 2D canvas coords (x,y) to 3D scene coords (x, y, z)
// In 3D, Y is up, X is right, Z is towards viewer.
// Canvas has (0,0) at top-left, going right(X) and down(Y).
// So X_3d = x - W/2, Z_3d = y - H/2.
const to3D = (x, y, h = 0) => [x - W / 2, h, y - H / 2];

// Component to render the static warehouse environment
const Environment = ({ mapData }) => {
  const paths = useMemo(() => {
    if (!mapData?.edges) return [];
    return mapData.edges.map(edge => {
      const from = mapData.nodes.find(n => n.id === edge.from);
      const to = mapData.nodes.find(n => n.id === edge.to);
      if (from && to) {
        return { start: to3D(from.x, from.y, 0.5), end: to3D(to.x, to.y, 0.5) };
      }
      return null;
    }).filter(Boolean);
  }, [mapData]);

  return (
    <group>
      {/* Floor */}
      <Plane args={[W, H]} rotation={[-Math.PI / 2, 0, 0]} position={[0, -0.1, 0]} receiveShadow>
        <meshStandardMaterial color="#f8fafc" />
      </Plane>
      
      {/* Paths */}
      {paths.map((p, i) => (
        <Line key={i} points={[p.start, p.end]} color="#94a3b8" lineWidth={1} dashed dashSize={4} gapSize={4} />
      ))}

      {/* Areas (Storage, Pickup, Dropoff) */}
      {mapData?.areas?.map((area, i) => {
        let color = "#8b5cf6"; // Purple (Storage)
        let height = 30;
        if (area.type === 'pickup') { color = "#10b981"; height = 2; }
        if (area.type === 'dropoff') { color = "#ef4444"; height = 2; }
        
        // Center of area
        const cx = area.x + area.w / 2;
        const cy = area.y + area.h / 2;
        const [x3, y3, z3] = to3D(cx, cy, height / 2);
        
        return (
          <Box key={`area-${i}`} args={[area.w, height, area.h]} position={[x3, y3, z3]} receiveShadow castShadow>
            <meshStandardMaterial color={color} transparent opacity={0.6} />
          </Box>
        );
      })}
    </group>
  );
};

// Component to render dynamic pallets and AGVs
const DynamicEntities = ({ engine, tick, config }) => {
  if (!engine) return null;
  const state = engine.getState();
  const primaryColor = config?.agvColor || '#f97316';
  
  // Render Pallets at Virtual Slots
  const pallets = state.virtualSlots?.map(slot => {
    if (state.occupiedNodes?.has(slot.id)) {
      // In storage
      const [x, y, z] = to3D(slot.x, slot.y, 30 + 6); // On top of rack
      return <Box key={`pallet-${slot.id}`} args={[12, 12, 12]} position={[x, y, z]} castShadow><meshStandardMaterial color="#f59e0b" /></Box>;
    }
    return null;
  }).filter(Boolean);

  // Render Waiting Pallets at Pickup
  const waitingPallets = state.orders?.map(order => {
    const isPickedUp = state.agvs.some(agv => agv.payload && agv.payload.id === order.id && agv.state === 'moving_to_storage');
    if (!isPickedUp) {
      const slot = state.virtualSlots?.find(s => s.id === order.pickupNodeId);
      if (slot) {
        const [x, y, z] = to3D(slot.x, slot.y, 2 + 6); // On top of pickup platform
        return <Box key={`waiting-${order.id}`} args={[12, 12, 12]} position={[x, y, z]} castShadow><meshStandardMaterial color="#10b981" /></Box>;
      }
    }
    return null;
  }).filter(Boolean);

  // Render AGVs
  const agvs = state.agvs?.map(agv => {
    const [x, y, z] = to3D(agv.x, agv.y, 10);
    const color = agv.state === 'idle' ? '#64748b' : primaryColor;
    
    // Optional: Calculate rotation if we have targetNodeId to make AGV face movement direction
    // For now, keeping rotation fixed or simplified.
    
    return (
      <group key={agv.id} position={[x, y, z]}>
        {/* Forklift Base Chassis */}
        <Box args={[20, 10, 32]} position={[0, 0, 0]} castShadow receiveShadow>
          <meshStandardMaterial color={color} />
        </Box>
        
        {/* Forklift Cabin / Roof */}
        <Box args={[16, 2, 20]} position={[0, 20, 2]} castShadow>
          <meshStandardMaterial color="#1f2937" />
        </Box>
        {/* Cabin Pillars */}
        <Box args={[2, 12, 2]} position={[-7, 13, -7]} castShadow><meshStandardMaterial color="#374151" /></Box>
        <Box args={[2, 12, 2]} position={[7, 13, -7]} castShadow><meshStandardMaterial color="#374151" /></Box>
        <Box args={[2, 12, 2]} position={[-7, 13, 11]} castShadow><meshStandardMaterial color="#374151" /></Box>
        <Box args={[2, 12, 2]} position={[7, 13, 11]} castShadow><meshStandardMaterial color="#374151" /></Box>

        {/* Forklift Mast & Forks (Front) */}
        <Box args={[16, 26, 4]} position={[0, 8, -18]} castShadow receiveShadow>
          <meshStandardMaterial color="#475569" />
        </Box>
        <Box args={[4, 2, 20]} position={[-5, -3, -30]} castShadow receiveShadow>
          <meshStandardMaterial color="#94a3b8" />
        </Box>
        <Box args={[4, 2, 20]} position={[5, -3, -30]} castShadow receiveShadow>
          <meshStandardMaterial color="#94a3b8" />
        </Box>
        
        {/* Wheels (Black) */}
        <Cylinder args={[4, 4, 4, 32]} rotation={[0, 0, Math.PI / 2]} position={[-11, -3, -10]} castShadow><meshStandardMaterial color="#111827" /></Cylinder>
        <Cylinder args={[4, 4, 4, 32]} rotation={[0, 0, Math.PI / 2]} position={[11, -3, -10]} castShadow><meshStandardMaterial color="#111827" /></Cylinder>
        <Cylinder args={[4, 4, 4, 32]} rotation={[0, 0, Math.PI / 2]} position={[-11, -3, 10]} castShadow><meshStandardMaterial color="#111827" /></Cylinder>
        <Cylinder args={[4, 4, 4, 32]} rotation={[0, 0, Math.PI / 2]} position={[11, -3, 10]} castShadow><meshStandardMaterial color="#111827" /></Cylinder>

        {/* Label */}
        <Text position={[0, 25, 0]} fontSize={8} color="#ffffff" outlineWidth={1} outlineColor="#000000">
          {agv.id.replace('agv_', '')}
        </Text>
        {/* Payload on Forks */}
        {agv.payload && (
          <Box args={[12, 12, 12]} position={[0, 4, -28]} castShadow>
            <meshStandardMaterial color="#f59e0b" />
          </Box>
        )}
      </group>
    );
  });

  return (
    <group>
      {pallets}
      {waitingPallets}
      {agvs}
    </group>
  );
};

// Main 3D View Component
export default function SimulationView3D({ mapData, engine, config }) {
  // Use a state to force re-render similar to requestAnimationFrame loop in 2D
  const [tick, setTick] = React.useState(0);
  
  React.useEffect(() => {
    let animationFrameId;
    const loop = () => {
      setTick(t => t + 1);
      animationFrameId = requestAnimationFrame(loop);
    };
    loop();
    return () => cancelAnimationFrame(animationFrameId);
  }, []);

  return (
    <div style={{ width: '100%', height: '100%', background: '#1e293b' }}>
      <Canvas shadows camera={{ position: [0, 400, 600], fov: 50, near: 1, far: 5000 }}>
        <ambientLight intensity={0.5} />
        <directionalLight position={[200, 500, 300]} intensity={1.5} castShadow shadow-mapSize={[2048, 2048]} />
        <pointLight position={[-200, 300, -200]} intensity={0.8} />
        
        <Environment mapData={mapData} />
        <DynamicEntities engine={engine} tick={tick} config={config} />
        
        <OrbitControls makeDefault maxPolarAngle={Math.PI / 2 - 0.05} />
      </Canvas>
    </div>
  );
}
